import React, { useCallback, useMemo, useState } from 'react';
import { useController } from 'react-hook-form';
import TextField from '@mui/material/TextField';
import InputBase, { InputBaseProps } from '@mui/material/InputBase';
import IconButton from '@mui/material/IconButton';
import InputAdornment from '@mui/material/InputAdornment';
import VisibilityOutlined from '@mui/icons-material/VisibilityOutlined';
import VisibilityOffOutlined from '@mui/icons-material/VisibilityOffOutlined';
import Typography from '@mui/material/Typography';
import isEmpty from 'lodash/isEmpty';
import { useTranslation } from 'react-i18next';

import styles from './style';

// Plain input (no form): a controlled InputBase styled by the caller (ex: a search, a textarea, a date).
type PlainInputProps = InputBaseProps;

const PlainInput: React.FC<PlainInputProps> = React.memo((props) => <InputBase {...props} />);

interface FormInputProps {
  alreadyExist?: boolean;
  // Browser autofill hint (ex: current-password on the login, new-password on the sign up).
  autoComplete?: string;
  autoFocus?: boolean;
  control: any;
  // Dark style of the connected pages and the auth pages.
  dark?: boolean;
  defaultValue?: string;
  disabled?: boolean;
  // An error that doesn't come from the rules (ex: a wrong login): true = red border only, a text = border + message.
  forcedError?: boolean | string;
  fullWidth?: boolean;
  id?: string;
  innerRef?: any;
  label?: string;
  multiline?: boolean;
  name: string;
  onBlur?: (e: any) => void;
  onChange?: (e: any) => void;
  onFocus?: (e: any) => void;
  placeholder: string;
  required?: boolean;
  rules: any;
  size?: 'medium' | 'small';
  sx?: {};
  styleContainer?: {};
  type?: string;
  variant?: 'filled' | 'outlined' | 'standard';
  value?: string;
}

// Input bound to react-hook-form (TextField with the rules, the errors and the password eye).
const FormInput: React.FC<FormInputProps> = React.memo((props) => {
  const {
    alreadyExist = false,
    autoComplete,
    autoFocus,
    control,
    dark,
    disabled,
    forcedError,
    fullWidth,
    id,
    label,
    multiline,
    name,
    onChange,
    onBlur,
    onFocus,
    placeholder,
    required,
    rules = {},
    size = 'medium',
    sx,
    type,
    variant = 'outlined',
    value: valueInput,
  } = props;
  const { t } = useTranslation();

  let defaultValue = '';
  if (valueInput) {
    defaultValue = valueInput;
  }

  const { field, fieldState } = useController({
    control,
    name,
    rules,
    defaultValue,
  });
  const { value, onChange: onChangeField } = field;
  const { error } = fieldState;
  const hasError = !isEmpty(error) || !!forcedError;
  const forcedErrorText = typeof forcedError === 'string' ? forcedError : '';
  // A password can be shown while typing (eye at the end of the field).
  const isPassword = type === 'password';
  const [isPasswordVisible, setIsPasswordVisible] = useState(false);
  const inputType = isPassword && isPasswordVisible ? 'text' : type;

  const togglePasswordVisible = useCallback(() => {
    setIsPasswordVisible((prev) => !prev);
  }, []);

  const inputProps = useMemo(() => {
    if (!isPassword) {
      return undefined;
    }
    return {
      endAdornment: (
        <InputAdornment position="end">
          <IconButton
            edge="end"
            onClick={togglePasswordVisible}
            aria-label={isPasswordVisible ? t('form.hidePassword') : t('form.showPassword')}
            sx={dark ? styles.eyeDark : styles.eye}
          >
            {isPasswordVisible ? <VisibilityOffOutlined fontSize="small" /> : <VisibilityOutlined fontSize="small" />}
          </IconButton>
        </InputAdornment>
      ),
    };
  }, [dark, isPassword, isPasswordVisible, t, togglePasswordVisible]);

  const errorTrad = t('error');

  const onChangeText = useCallback((e: any) => {
    if (onChange) {
      onChange(e);
    }
    onChangeField(e);
  }, [onChange, onChangeField]);

  const containerStyle = useMemo(() => {
    if (dark) {
      return styles.inputDark as React.CSSProperties;
    }
    return styles.input as React.CSSProperties;
  }, [dark]);

  const textFieldSx = useMemo(() => {
    if (dark) {
      return { ...styles.textFieldDark, ...sx };
    }
    return undefined;
  }, [dark, sx]);

  const errorSx = useMemo(() => {
    if (dark) {
      return styles.errorDark;
    }
    return styles.error;
  }, [dark]);

  return (
    <div style={containerStyle}>
      <TextField
        sx={textFieldSx}
        error={hasError}
        disabled={disabled}
        name={name}
        required={required}
        fullWidth={fullWidth}
        // The label stays the name of the field: the red border and the message below show the error.
        label={label}
        id={id}
        size={size}
        value={value}
        placeholder={placeholder}
        autoComplete={autoComplete}
        autoFocus={autoFocus}
        multiline={multiline}
        type={inputType}
        InputProps={inputProps}
        variant={variant}
        onChange={onChangeText}
        onBlur={onBlur}
        onFocus={onFocus}
      />
      {!alreadyExist && error && (
        <Typography variant="body2" sx={errorSx}>
          {error.message || errorTrad}
        </Typography>
      )}
      {!alreadyExist && !error && forcedErrorText && (
        <Typography variant="body2" sx={errorSx}>
          {forcedErrorText}
        </Typography>
      )}
      {alreadyExist && (
        <Typography variant="body2" sx={errorSx}>
          {t(`form.error.alreadyExist.${name}`)}
        </Typography>
      )}
    </div>
  );
});

type CustomInputProps = FormInputProps | PlainInputProps;

const isFormInput = (props: CustomInputProps): props is FormInputProps => 'control' in props && !!props.control;

// With a `control`: a form field (react-hook-form). Without: a plain controlled input (value / onChange).
const CustomInput: React.FC<CustomInputProps> = React.memo((props) => {
  if (isFormInput(props)) {
    return <FormInput {...props} />;
  }
  return <PlainInput {...props} />;
});

export default CustomInput;
